/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';

import { currentAdminGroupStateVar } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../../../logger';
import {
    CreateAdminGroupOutput,
    CreateConsistencyAdminGroupClient,
    AdminGroupState,
    ReCreateConsistencyAdminGroupClient,
} from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from '../PreSetupTemplateSteps';
import {
    CreateAdminGroupActions,
    CreateAdminGroupComponent,
    CreateAdminGroupComponentProps,
} from './CreateAdminGroup.component';

export const CreateAdminGroupContainer: React.FC<PreSetupTemplateStepProps> = (
    props: PreSetupTemplateStepProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const restoreGroupEnabled = host.getEcsConfig().MicrosoftTeamApprovals.RestoreGroupEnabled;
    const { navigateTo, done } = props;
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showError, setShowError] = React.useState<boolean>(false);
    const [adminGroup, setAdminGroup] = React.useState<CreateAdminGroupOutput>(null);
    const currentAdminGroupState = useReactiveVar<AdminGroupState>(currentAdminGroupStateVar);

    const onNext = (currentAction: CreateAdminGroupActions): void => {
        if (currentAction === CreateAdminGroupActions.create || currentAction === CreateAdminGroupActions.recreate) {
            const startTime = performance.now();

            logger.markScenario(
                ScenarioName.CreateTemplateInstance,
                StepName.CreateAdminGroupButtonPressed,
                StatusName.NA
            );
            logger.logUserBiAction(
                SubWorkLoad.AppInteraction,
                ScenarioName.CreateTemplateInstance,
                StepName.CreateAdminGroupButtonPressed,
                'click',
                'submit',
                'createAdminGroup',
                'createAdminGroupButton',
                PanelRegion.Modal,
                PanelType.PreSetupTemplateInstance
            );

            setIsLoading(true);

            if (restoreGroupEnabled) {
                new ReCreateConsistencyAdminGroupClient()
                    .createConsistencyAdminGroup()
                    .then((result: CreateAdminGroupOutput): any => {
                        setTimeout((): void => {
                            setAdminGroup(result);
                            currentAdminGroupStateVar(AdminGroupState.Exist);
                            setIsLoading(false);
                        }, 10000);

                        logger.markScenario(
                            ScenarioName.CreateTemplateInstance,
                            StepName.CreateAdminGroupButtonPressed,
                            StatusName.Success,
                            {
                                stepElapsed: performance.now() - startTime,
                            }
                        );
                    })
                    .catch((error: any): void => {
                        logger.logTraceHttpException('createAdminGroup', error?.message, error?.status);
                        logger.markScenario(
                            ScenarioName.CreateTemplateInstance,
                            StepName.CreateAdminGroupButtonPressed,
                            StatusName.Fail,
                            {
                                stepElapsed: performance.now() - startTime,
                            }
                        );

                        setShowError(true);
                        setIsLoading(false);
                    });
            } else {
                new CreateConsistencyAdminGroupClient()
                    .createConsistencyAdminGroup()
                    .then((result: CreateAdminGroupOutput): any => {
                        setTimeout((): void => {
                            currentAdminGroupStateVar(AdminGroupState.Exist);
                            setAdminGroup(result);
                            setIsLoading(false);
                        }, 10000);

                        logger.markScenario(
                            ScenarioName.CreateTemplateInstance,
                            StepName.CreateAdminGroupButtonPressed,
                            StatusName.Success,
                            {
                                stepElapsed: performance.now() - startTime,
                            }
                        );
                    })
                    .catch((error: any): void => {
                        logger.logTraceHttpException('createAdminGroup', error?.message, error?.status);
                        logger.markScenario(
                            ScenarioName.CreateTemplateInstance,
                            StepName.CreateAdminGroupButtonPressed,
                            StatusName.Fail,
                            {
                                stepElapsed: performance.now() - startTime,
                            }
                        );

                        setShowError(true);
                        setIsLoading(false);
                    });
            }
        } else {
            done();
        }
    };

    const onBack = (): void => {
        navigateTo(PreSetupTemplateSteps.SelectScope);
    };

    let actionType = CreateAdminGroupActions.create;

    if (currentAdminGroupState === AdminGroupState.Corrupt) {
        actionType = CreateAdminGroupActions.recreate;
    } else if (currentAdminGroupState === AdminGroupState.Exist) {
        actionType = CreateAdminGroupActions.done;
    } else if (currentAdminGroupState === AdminGroupState.NotCreated) {
        actionType = CreateAdminGroupActions.create;
    }

    const componentProps: CreateAdminGroupComponentProps = {
        isLoading,
        action: actionType,
        onNext,
        onBack,
        showError,
        groupName: adminGroup && adminGroup.GroupName,
    };

    return <CreateAdminGroupComponent {...componentProps} />;
};
